import i18next, { TFunction } from "i18next";
import pdfMake from "pdfmake";
import { accent, recommendationStatusColors } from "../config/colors";
import { TaxonomyState } from "../store/taxonomy/taxonomy";
import { ExpandedColumn } from "../types/columns";
import { ClientLocation, RecommendationAzure, Report } from "../types/types";
import { formatDateWithTime, formatDateString } from "./utils";
import { formatNumber } from "./number";

export const createPDFRecommendations = ({
  recommendations,
  columns,
  taxonomy,
  t,
}: {
  recommendations: RecommendationAzure[];
  columns: ExpandedColumn[];
  taxonomy: TaxonomyState;
  t: TFunction;
}) => {
  const docDefinition = {
    pageOrientation: "landscape",
    pageMargins: [20, 20, 20, 20],
    pageSize: {
      width:
        columns.reduce((sum, c) => {
          if (c.pdf && c.pdf.width !== undefined) {
            return sum + c.pdf.width;
          }
          return sum + c.width;
        }, 0) * 1.1,
      height: 1200,
    },
    info: {
      title: `${t("ram.selectMenu.recommendations")} ${formatDateWithTime(
        new Date(),
      )}`,
    },
    content: [
      {
        columns: [
          {
            width: 300,
            stack: [
              {
                columns: [
                  {
                    text: "",
                    style: {
                      font: "Remix",
                      color:
                        recommendationStatusColors[
                          "RiskRecommendationStatus.WAITING_FOR_GREG"
                        ],
                      fontSize: 10,
                    },
                    width: 10,
                  },
                  {
                    text: t("RiskRecommendationStatus.WAITING_FOR_GREG"),
                  },
                ],
                columnGap: 2,
              },
              {
                columns: [
                  {
                    text: "",
                    style: {
                      font: "LineAwesome",
                      color:
                        recommendationStatusColors[
                          "RiskRecommendationStatus.WAITING_FOR_GREG"
                        ],
                      fontSize: 13,
                    },
                    width: 13,
                  },
                  {
                    text: `${t(
                      "RiskRecommendationStatus.WAITING_FOR_GREG",
                    )} ${t("greco.and")} ${t("ram.assessed")}`,
                  },
                ],
                columnGap: 2,
              },
              {
                columns: [
                  {
                    text: "",
                    style: {
                      font: "Remix",
                      color: accent.orangeLighter,
                      fontSize: 10,
                    },
                    width: 10,
                  },
                  {
                    text: `${t(
                      "RiskRecommendationStatus.WAITING_FOR_GREG",
                    )} ${t("greco.and")} ${t("ram.assessmentPeriodStarted")}`,
                  },
                ],
                columnGap: 2,
              },
              {
                columns: [
                  {
                    text: "",
                    style: {
                      font: "LineAwesome",
                      color: accent.orangeLighter,
                      fontSize: 13,
                    },
                    width: 13,
                  },
                  {
                    text: `${t(
                      "RiskRecommendationStatus.WAITING_FOR_GREG",
                    )}, ${t("ram.assessmentPeriodStarted")} ${t(
                      "greco.and",
                    )} ${t("ram.assessed")}`,
                  },
                ],
                columnGap: 2,
              },
              {
                columns: [
                  {
                    text: "",
                    style: {
                      font: "Remix",
                      color:
                        recommendationStatusColors[
                          "RiskRecommendationStatus.WAITING_FOR_CLIENT"
                        ],
                      fontSize: 10,
                    },
                    width: 10,
                  },
                  {
                    text: t("RiskRecommendationStatus.WAITING_FOR_CLIENT"),
                  },
                ],
                columnGap: 2,
              },
              {
                columns: [
                  {
                    text: "",
                    style: {
                      font: "Remix",
                      color:
                        recommendationStatusColors[
                          "RiskRecommendationStatus.CLOSED"
                        ],
                      fontSize: 10,
                    },
                    width: 10,
                  },
                  {
                    text: t("RiskRecommendationStatus.CLOSED"),
                  },
                ],
                columnGap: 2,
              },
            ],
          },
          // {
          //   stack: [
          //     {
          //       columns: [
          //         {
          //           text: "",
          //           style: {
          //             font: "Remix",
          //             color: accent.redDark,
          //             fontSize: 17,
          //           },
          //           width: 17,
          //         },
          //         {
          //           text: t("greco.required"),
          //         },
          //       ],
          //       columnGap: 2,
          //     },
          //     {
          //       columns: [
          //         {
          //           text: "",
          //           style: {
          //             font: "LineAwesome",
          //             color: accent.redDark,
          //             fontSize: 17,
          //           },
          //           width: 17,
          //         },
          //         {
          //           text: t("ram.recommendations.hasDraft"),
          //         },
          //       ],
          //       columnGap: 2,
          //     },
          //     {
          //       columns: [
          //         {
          //           text: "",
          //           style: {
          //             font: "LineAwesome",
          //             color: accent.redDark,
          //             fontSize: 17,
          //           },
          //           width: 17,
          //         },
          //         {
          //           text: t("greco.overdue"),
          //         },
          //       ],
          //       columnGap: 2,
          //     },
          //     {
          //       columns: [
          //         {
          //           text: "",
          //           style: {
          //             font: "LineAwesome",
          //             color: accent.redDark,
          //             fontSize: 17,
          //           },
          //           width: 17,
          //         },
          //         {
          //           text: t("ram.recommendations.plannedImplementationLate"),
          //         },
          //       ],
          //       columnGap: 2,
          //     },
          //   ],
          // },
        ],
      },
      { text: " " },
      {
        table: {
          headerRows: 1,
          widths: columns.map((c) => {
            if (c.pdf && c.pdf.width !== undefined) {
              return c.pdf.width;
            }
            return c.width;
          }),
          body: [
            columns.map((c) => {
              if (c.pdf && c.pdf.label !== undefined) {
                return {
                  text: c.pdf.label,
                  style: "headerCell",
                };
              }
              return {
                text: i18next.t(c.labelKey),
                style: "headerCell",
              };
            }),
          ].concat(
            recommendations.map((r) => {
              return columns.map((c) => {
                const value = r[c.key];
                if (c.pdf && c.pdf.render) {
                  try {
                    return {
                      style: "bodyCell",
                      ...c.pdf.render({
                        recommendation: r,
                        taxonomy,
                        t: i18next.t.bind(i18next),
                      }),
                    };
                  } catch (err) {
                    console.log(err);
                  }
                }
                if (value === undefined || value === null) return "";
                return {
                  text: value,
                  style: "bodyCell",
                };
              });
            }),
          ),
        },
      },
    ],
    styles: {
      headerCell: {
        fillColor: "#235592",
        color: "#fff",
        bold: true,
        alignment: "center",
      },
      bodyCell: {
        alignment: "center",
      },
    },
    defaultStyle: {
      font: "SegoeUI",
    },
  };

  return pdfMake.createPdf(docDefinition);
};

export const createPDFLocations = ({
  locations,
  taxonomy,
  t,
  visibleLabelKeys,
}: {
  locations: ClientLocation[];
  taxonomy: TaxonomyState;
  t: TFunction;
  visibleLabelKeys?: string[];
}) => {
  const allColumns = [
    {
      key: "locationName",
      labelKey: "ram.locationList.locationName",
      width: 100,
    },
    { key: "clientName", labelKey: "ram.clientsList.clientName", width: 100 },
    {
      key: "webBasePartnerNumber",
      labelKey: "ram.clientsList.clientId",
      width: 80,
    },
    { key: "siccode", labelKey: "ram.locationList.sicCode", width: 80 },
    { key: "countryCode", labelKey: "ram.locationList.countryCode", width: 60 },
    { key: "postalCode", labelKey: "ram.locationList.postalCode", width: 60 },
    { key: "city", labelKey: "ram.locationList.city", width: 80 },
    {
      key: "turnoverInMioEur",
      labelKey: "ram.locationList.turnoverInMioEur",
      width: 60,
    },
    { key: "tsiinMioEur", labelKey: "ram.locationList.tsiinMioEur", width: 60 },
    { key: "pmlinMioEur", labelKey: "ram.locationList.pmlinMioEur", width: 60 },
    { key: "emlinMioEur", labelKey: "ram.locationList.emlinMioEur", width: 60 },
    {
      key: "ebitinMioEur",
      labelKey: "ram.locationList.ebitinMioEur",
      width: 60,
    },
    {
      key: "ygpcminMioEur",
      labelKey: "ram.locationList.ygpcminMioEur",
      width: 60,
    },
    {
      key: "deductiblesInMioEur",
      labelKey: "ram.locationList.deductiblesinMioEur",
      width: 60,
    },
    { key: "coords", labelKey: "ram.locationList.coordinates", width: 80 },
    {
      key: "insurersRaiting",
      labelKey: "ram.locationList.insurersRating",
      width: 60,
    },
  ];
  const columns = visibleLabelKeys
    ? allColumns.filter((c) => visibleLabelKeys.includes(c.labelKey))
    : allColumns;

  const totalWidth = columns.reduce((sum, c) => sum + c.width, 0);
  const pageWidth = totalWidth + 40 + columns.length * 10; // margins + cell padding/borders

  const docDefinition = {
    pageOrientation: "landscape",
    pageMargins: [20, 20, 20, 20],
    pageSize: {
      width: pageWidth,
      height: 1200,
    },
    info: {
      title: `${t("ram.selectMenu.locations")} ${formatDateWithTime(new Date())}`,
    },
    content: [
      {
        table: {
          headerRows: 1,
          widths: columns.map((c) => c.width),
          body: [
            columns.map((c) => ({
              text: t(c.labelKey),
              style: "headerCell",
            })),
          ].concat(
            locations.map((loc) =>
              columns.map((c) => {
                let value: any = "";
                switch (c.key) {
                  case "siccode":
                    value = taxonomy.SicCode.byId[loc.siccode]?.code
                      ? t(taxonomy.SicCode.byId[loc.siccode].code)
                      : loc.siccode || "";
                    break;
                  case "countryCode":
                    value = taxonomy.Country.byId[loc.countryCode]?.code
                      ? t(taxonomy.Country.byId[loc.countryCode].code)
                      : loc.countryCode || "";
                    break;
                  case "turnoverInMioEur":
                  case "tsiinMioEur":
                  case "pmlinMioEur":
                  case "emlinMioEur":
                  case "ebitinMioEur":
                  case "ygpcminMioEur":
                  case "deductiblesInMioEur":
                    value = loc[c.key] != null ? formatNumber(loc[c.key]) : "";
                    break;
                  case "coords":
                    value = loc.latitude
                      ? `${loc.latitude}, ${loc.longitude}`
                      : "";
                    break;
                  default:
                    value = loc[c.key] ?? "";
                }
                return { text: String(value), style: "bodyCell" };
              }),
            ),
          ),
        },
      },
    ],
    styles: {
      headerCell: {
        fillColor: "#235592",
        color: "#fff",
        bold: true,
        alignment: "center",
        fontSize: 8,
      },
      bodyCell: {
        alignment: "center",
        fontSize: 8,
      },
    },
    defaultStyle: {
      font: "SegoeUI",
    },
  };

  return pdfMake.createPdf(docDefinition);
};

export const createPDFReports = ({
  reports,
  taxonomy,
  t,
  visibleLabelKeys,
}: {
  reports: Report[];
  taxonomy: TaxonomyState;
  t: TFunction;
  visibleLabelKeys?: string[];
}) => {
  const allColumns = [
    { key: "reportName", labelKey: "ram.reportList.reportName", width: 100 },
    { key: "clientName", labelKey: "ram.clientsList.clientName", width: 100 },
    {
      key: "webBasePartnerNumber",
      labelKey: "ram.clientsList.clientId",
      width: 80,
    },
    {
      key: "locationName",
      labelKey: "ram.locationList.locationName",
      width: 100,
    },
    { key: "surveyDate", labelKey: "ram.reportList.surveyDate", width: 70 },
    { key: "pmlinMioEur", labelKey: "ram.locationList.pmlinMioEur", width: 60 },
    { key: "emlinMioEur", labelKey: "ram.locationList.emlinMioEur", width: 60 },
    {
      key: "insurersRaiting",
      labelKey: "ram.locationList.insurersRating",
      width: 60,
    },
    {
      key: "reportCreatorCode",
      labelKey: "ram.reportList.reportCreatorCode",
      width: 80,
    },
    {
      key: "isReportDeactivated",
      labelKey: "ram.reportList.isActive",
      width: 60,
    },
  ];
  const columns = visibleLabelKeys
    ? allColumns.filter((c) => visibleLabelKeys.includes(c.labelKey))
    : allColumns;

  const docDefinition = {
    pageOrientation: "landscape",
    pageMargins: [20, 20, 20, 20],
    pageSize: {
      width: columns.reduce((sum, c) => sum + c.width, 0) * 1.1,
      height: 1200,
    },
    info: {
      title: `${t("ram.selectMenu.reports")} ${formatDateWithTime(new Date())}`,
    },
    content: [
      {
        table: {
          headerRows: 1,
          widths: columns.map((c) => c.width),
          body: [
            columns.map((c) => ({
              text: t(c.labelKey),
              style: "headerCell",
            })),
          ].concat(
            reports.map((report) =>
              columns.map((c) => {
                let value: any = "";
                switch (c.key) {
                  case "surveyDate":
                    value = report.surveyDate
                      ? formatDateString(report.surveyDate)
                      : "";
                    break;
                  case "pmlinMioEur":
                  case "emlinMioEur":
                    value =
                      report[c.key] != null ? formatNumber(report[c.key]) : "";
                    break;
                  case "reportCreatorCode":
                    value = taxonomy.RiskReportCreator.byId[
                      report.reportCreatorCode
                    ]?.code
                      ? t(
                          taxonomy.RiskReportCreator.byId[
                            report.reportCreatorCode
                          ].code,
                        )
                      : report.reportCreatorCode || "";
                    break;
                  case "isReportDeactivated":
                    value = report.isReportDeactivated
                      ? t("greco.no")
                      : t("greco.yes");
                    break;
                  default:
                    value = report[c.key] ?? "";
                }
                return { text: String(value), style: "bodyCell" };
              }),
            ),
          ),
        },
      },
    ],
    styles: {
      headerCell: {
        fillColor: "#235592",
        color: "#fff",
        bold: true,
        alignment: "center",
        fontSize: 8,
      },
      bodyCell: {
        alignment: "center",
        fontSize: 8,
      },
    },
    defaultStyle: {
      font: "SegoeUI",
    },
  };

  return pdfMake.createPdf(docDefinition);
};

export const createPDFClients = ({
  clients,
  taxonomy,
  t,
  visibleLabelKeys,
}: {
  clients: any[];
  taxonomy: TaxonomyState;
  t: TFunction;
  visibleLabelKeys?: string[];
}) => {
  const allColumns = [
    { key: "clientName", labelKey: "ram.clientsList.clientName", width: 150 },
    { key: "clientId", labelKey: "ram.clientsList.clientId", width: 100 },
    {
      key: "countryCodeID",
      labelKey: "ram.clientList.countryCode",
      width: 100,
    },
    {
      key: "costCenterCodeID",
      labelKey: "ram.clientsList.costCenterCode",
      width: 100,
    },
    {
      key: "goodRangeMin",
      labelKey: "ram.clientsList.goodRangeMin",
      width: 80,
    },
    {
      key: "moderateRangeMin",
      labelKey: "ram.clientsList.moderateRangeMin",
      width: 80,
    },
    {
      key: "poorRangeMin",
      labelKey: "ram.clientsList.poorRangeMin",
      width: 80,
    },
    {
      key: "mediumCostEstimateMin",
      labelKey: "ram.clientsList.mediumLowThreshold",
      width: 70,
    },
    {
      key: "highCostEstimateMin",
      labelKey: "ram.clientsList.highMediumThreshold",
      width: 70,
    },
  ];
  const columns = visibleLabelKeys
    ? allColumns.filter(
        (c) =>
          c.labelKey === "ram.clientsList.clientName" ||
          visibleLabelKeys.includes(c.labelKey),
      )
    : allColumns;

  const docDefinition = {
    pageOrientation: "landscape",
    pageMargins: [20, 20, 20, 20],
    pageSize: {
      width: columns.reduce((sum, c) => sum + c.width, 0) * 1.1,
      height: 1200,
    },
    info: {
      title: `${t("ram.selectMenu.clientsTree")} ${formatDateWithTime(new Date())}`,
    },
    content: [
      {
        table: {
          headerRows: 1,
          widths: columns.map((c) => c.width),
          body: [
            columns.map((c) => ({
              text: t(c.labelKey),
              style: "headerCell",
            })),
          ].concat(
            clients.map((client) =>
              columns.map((c) => {
                let value: any = "";
                switch (c.key) {
                  case "countryCodeID":
                    value = taxonomy.Country.byId[client.countryCodeID]?.code
                      ? t(taxonomy.Country.byId[client.countryCodeID].code)
                      : client.countryCodeID || "";
                    break;
                  case "costCenterCodeID":
                    value = taxonomy.CostCenter.byId[client.costCenterCodeID]
                      ?.code
                      ? t(
                          taxonomy.CostCenter.byId[client.costCenterCodeID]
                            .code,
                        )
                      : client.costCenterCodeID || "";
                    break;
                  default:
                    value = client[c.key] ?? "";
                }
                return { text: String(value), style: "bodyCell" };
              }),
            ),
          ),
        },
      },
    ],
    styles: {
      headerCell: {
        fillColor: "#235592",
        color: "#fff",
        bold: true,
        alignment: "center",
        fontSize: 9,
      },
      bodyCell: {
        alignment: "center",
        fontSize: 9,
      },
    },
    defaultStyle: {
      font: "SegoeUI",
    },
  };

  return pdfMake.createPdf(docDefinition);
};
